"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type HotYogaDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * A heated studio mid-class: infrared panels glowing along the ceiling, the
 * room thermostat reading 40° on the back wall (the focal point), heat haze
 * rising off the floor, and three mats laid out in a row with towels and
 * bottles. The middle mat ends just above the bottom-centre participant pill.
 *
 * Motion: the heaters pulse and two haze layers rise — CSS transform/opacity
 * only, off under reduced motion.
 */
const HOT_YOGA_KEYFRAMES = `
@keyframes hot-yoga-haze {
  0% { transform: translate3d(0, 10px, 0); opacity: 0 }
  35% { opacity: 1 }
  100% { transform: translate3d(0, -26px, 0); opacity: 0 }
}
@keyframes hot-yoga-heater { 0%, 100% { opacity: 0.8 } 50% { opacity: 1 } }
.hot-yoga-haze { animation: hot-yoga-haze 5s ease-out infinite }
.hot-yoga-heater { animation: hot-yoga-heater 3.4s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .hot-yoga-haze { animation: none; opacity: 0.6 }
  .hot-yoga-heater { animation: none }
}
`;

/** Where the wall meets the floor, in px from the top of the scene. */
const HORIZON = 116;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `hot-yoga-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id("floor")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#a8532a" />
          <stop offset="1" stopColor="#4a1d0c" />
        </linearGradient>
        <linearGradient id={id("heater")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#ffe6a8" />
          <stop offset="0.5" stopColor="#ff7a2a" />
          <stop offset="1" stopColor="#d4300f" />
        </linearGradient>
        <linearGradient id={id("heat-cone")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#ff8a3d" stopOpacity="0.45" />
          <stop offset="1" stopColor="#ff8a3d" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={id("bottle")} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#d7f3ff" />
          <stop offset="1" stopColor="#7fb8d0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Wall panels, heater glow cones, floor — stretches with the card. */
function Studio({ id }: { id: IdFn }) {
  const h = HORIZON;
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 300 220"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {/* Cedar wall slats */}
      {Array.from({ length: 15 }, (_, i) => i * 20 + 10).map((x) => (
        <rect
          key={x}
          x={x}
          y="0"
          width="0.8"
          height={h}
          fill="#3a0f04"
          opacity="0.28"
        />
      ))}
      {/* Heat cones under the ceiling panels */}
      <g className="hot-yoga-heater">
        <polygon
          points={`18,14 92,14 118,${h} -8,${h}`}
          fill={`url(#${id("heat-cone")})`}
        />
        <polygon
          points={`208,14 282,14 308,${h} 182,${h}`}
          fill={`url(#${id("heat-cone")})`}
        />
      </g>
      {/* Skirting */}
      <rect x="0" y={h - 4} width="300" height="4" fill="#3a1206" />
      {/* Floor + boards toward a central vanishing point */}
      <rect
        x="0"
        y={h}
        width="300"
        height={220 - h}
        fill={`url(#${id("floor")})`}
      />
      {[-60, -10, 40, 90, 150, 210, 260, 310, 360].map((x) => (
        <line
          key={x}
          x1={150 + (x - 150) * 0.45}
          y1={h}
          x2={x}
          y2="220"
          stroke="#2a0c03"
          strokeOpacity="0.35"
          strokeWidth="0.8"
        />
      ))}
      {/* The heaters' reflection on the floor */}
      <ellipse
        cx="150"
        cy={h + 14}
        rx="150"
        ry="16"
        fill="#ff9a4a"
        opacity="0.18"
      />
    </svg>
  );
}

function Heater({ id, className }: { id: IdFn; className: string }) {
  return (
    <svg
      className={className}
      width="64"
      height="12"
      viewBox="0 0 76 12"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="0" width="76" height="12" rx="2" fill="#2a0e06" />
      <rect
        className="hot-yoga-heater"
        x="3"
        y="3"
        width="70"
        height="6"
        rx="1.5"
        fill={`url(#${id("heater")})`}
      />
      {[12, 24, 36, 48, 60].map((x) => (
        <rect
          key={x}
          x={x}
          y="3"
          width="0.8"
          height="6"
          fill="#7a1a06"
          opacity="0.6"
        />
      ))}
    </svg>
  );
}

/** Room thermostat — HTML so the digits get a real glow. */
function Thermostat() {
  return (
    <div className="absolute top-[44px] left-1/2 -translate-x-1/2">
      <div className="absolute -inset-x-8 -inset-y-6 rounded-full bg-[radial-gradient(closest-side,rgba(255,170,80,0.45),transparent)]" />
      <div className="relative flex items-center gap-1.5 rounded-full border border-white/15 bg-[linear-gradient(180deg,#2b120a,#160704)] py-1 pr-3 pl-1.5 shadow-[0_8px_20px_rgba(60,10,0,0.55),inset_0_1px_0_rgba(255,255,255,0.08)]">
        {/* Thermometer glyph */}
        <svg
          width="14"
          height="22"
          viewBox="0 0 14 22"
          aria-hidden="true"
          focusable="false"
        >
          <rect
            x="5"
            y="1"
            width="4"
            height="14"
            rx="2"
            fill="none"
            stroke="#ffd2a0"
            strokeOpacity="0.7"
            strokeWidth="1"
          />
          <rect x="6.2" y="5" width="1.6" height="10" fill="#ff5a2a" />
          <circle cx="7" cy="17" r="3.6" fill="#ff5a2a" />
        </svg>
        <span className="font-mono text-[22px] leading-none font-black text-[#ffb347] tabular-nums [text-shadow:0_0_8px_rgba(255,150,40,0.9),0_0_18px_rgba(255,110,30,0.5)]">
          40°
        </span>
        <span className="flex flex-col text-[6.5px] leading-[1.25] font-bold tracking-[0.14em] text-[#ffd9b0]/75">
          <span>CELSIUS</span>
          <span>RH 40%</span>
        </span>
      </div>
    </div>
  );
}

function Mat({
  className,
  color,
  edge,
  skew,
  towel,
}: {
  className: string;
  color: string;
  edge: string;
  skew: number;
  towel: string;
}) {
  return (
    <svg
      className={className}
      style={{ top: HORIZON + 8 }}
      width="72"
      height="42"
      viewBox="0 0 72 42"
      aria-hidden="true"
      focusable="false"
    >
      <polygon
        points={`${17 + skew},1 ${55 + skew},1 72,38 0,38`}
        fill="#000"
        opacity="0.25"
        transform="translate(0 3)"
      />
      <polygon
        points={`${17 + skew},0 ${55 + skew},0 72,37 0,37`}
        fill={color}
      />
      <polygon points="0,37 72,37 72,40 0,40" fill={edge} />
      <line
        x1={17 + skew}
        y1="0.5"
        x2={55 + skew}
        y2="0.5"
        stroke="#fff"
        strokeOpacity="0.4"
      />
      {/* Folded towel across the head of the mat */}
      <rect
        x={20 + skew * 0.9}
        y="2.5"
        width="32"
        height="7"
        rx="2.5"
        fill={towel}
      />
      <rect
        x={20 + skew * 0.9}
        y="2.5"
        width="32"
        height="2"
        rx="1"
        fill="#fff"
        opacity="0.5"
      />
    </svg>
  );
}

function Bottle({ id, className }: { id: IdFn; className: string }) {
  return (
    <svg
      className={className}
      style={{ top: HORIZON + 4 }}
      width="10"
      height="24"
      viewBox="0 0 10 24"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.5" y="0" width="5" height="4" rx="1" fill="#1d1d1f" />
      <rect
        x="0.5"
        y="3.5"
        width="9"
        height="20"
        rx="3"
        fill={`url(#${id("bottle")})`}
      />
      <rect x="0.5" y="10" width="9" height="6" fill="#ff6a3d" opacity="0.85" />
      <rect
        x="2"
        y="5"
        width="1.4"
        height="16"
        rx="0.7"
        fill="#fff"
        opacity="0.7"
      />
    </svg>
  );
}

/** Heat shimmer rising off each mat — the classic three wavy lines. */
function HeatHaze() {
  return (
    <>
      {[
        { pos: "left-[calc(17%-13px)]", delay: "0s" },
        { pos: "left-[calc(50%-13px)]", delay: "1.6s" },
        { pos: "left-[calc(83%-13px)]", delay: "3.2s" },
      ].map((haze) => (
        <svg
          key={haze.pos}
          className={`hot-yoga-haze absolute ${haze.pos}`}
          style={{ top: HORIZON - 30, animationDelay: haze.delay }}
          width="26"
          height="28"
          viewBox="0 0 26 28"
          aria-hidden="true"
          focusable="false"
        >
          {[5, 13, 21].map((x) => (
            <path
              key={x}
              d={`M${x} 27 Q${x - 4} 21 ${x} 14 T${x} 1`}
              stroke="#ffe7c2"
              strokeOpacity="0.55"
              strokeWidth="1.6"
              strokeLinecap="round"
              fill="none"
            />
          ))}
        </svg>
      ))}
    </>
  );
}

export function HotYogaDisplayPreview({
  participants,
}: HotYogaDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Hot Yoga"
      backgroundClassName="bg-[#8a2a10]"
      participants={participants}
      scene={
        <>
          <style href="hot-yoga-scene-keyframes" precedence="default">
            {HOT_YOGA_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Glowing amber wall, hottest up top */}
          <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_0%,#ff9a4a_0%,#d9541f_38%,#9a2c0e_70%,#5a1606_100%)]" />
          <Studio id={id} />

          <Heater id={id} className="absolute top-[10px] left-[3%]" />
          <Heater id={id} className="absolute top-[10px] right-[3%]" />
          <Thermostat />

          <HeatHaze />

          <Mat
            className="absolute left-[calc(17%-36px)]"
            color="#f0786a"
            edge="#a8443a"
            skew={10}
            towel="#fff4e0"
          />
          <Mat
            className="absolute left-[calc(50%-36px)]"
            color="#2fa39a"
            edge="#1a6660"
            skew={0}
            towel="#ffe9c8"
          />
          <Mat
            className="absolute left-[calc(83%-36px)]"
            color="#9b5fc0"
            edge="#5e3480"
            skew={-10}
            towel="#fff4e0"
          />
          <Bottle id={id} className="absolute left-[calc(17%+38px)]" />
          <Bottle id={id} className="absolute left-[calc(83%-48px)]" />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_110%_120%_at_50%_35%,transparent_50%,rgba(50,8,0,0.5)_100%)]" />
      }
    />
  );
}
